import { useDocumentTitleEntity } from '@components/DocumentTitle'
import { User } from '@components/user/User'
import { useCanAccessPath } from '@hooks/useCanAccessPath'
import { resetReservationQueryAndIds } from '@store/ReservationReducer'
import {
  getUserWithId,
  selectUserById,
  setSelectedUser,
} from '@store/UserReducer'
import { extractSectionFromPath } from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import {
  Flex,
  Layout,
  Space,
  Spin,
  Tabs,
  TabsProps,
  theme,
  Typography,
} from 'antd'
import { Content } from 'antd/es/layout/layout'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { LayoutFooter } from '../components/LayoutFooter'
import MainHeader from '../components/MainHeader'

const { Title } = Typography

export const UserDetailLayout: React.FC = () => {
  const { token } = theme.useToken()
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { userId } = useParams<{ userId: string }>()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [selectedKey, setSelectedKey] = useState<string>('generals')
  const user = useAppSelector(selectUserById(Number(userId)))
  useDocumentTitleEntity(
    isLoading || !user ? undefined : `${user.firstName} ${user.lastName}`
  )
  const { canAccessPath } = useCanAccessPath()

  const tabs: NonNullable<TabsProps['items']> = [
    {
      key: 'informations',
      label: 'Informations',
    },
    {
      key: 'transactions',
      label: 'Transactions',
    },
    {
      key: 'analytics',
      label: 'Analytics',
    },
    {
      key: 'membership',
      label: 'Membership',
    },
    {
      key: 'third-parties',
      label: 'Third-parties',
    },
    {
      key: 'activities',
      label: 'Activities',
    },
    {
      key: 'legals',
      label: 'Data & Legal',
    },
  ]

  // Each tab is a route under /users/:userId, so its gate is already declared in
  // ROUTE_PERMISSIONS - the strip reads that rather than keeping a list of its
  // own, which is what let it offer Transactions to the membership program
  // manager (holds `read user`, not `list reservation`) and Membership to
  // marketing and communication. A tab with nothing declared stays visible.
  const items: TabsProps['items'] = tabs.filter((tab) =>
    canAccessPath(`/users/${userId}/${tab.key}`)
  )

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 3))
  }, [pathname])

  useEffect(() => {
    if (!userId) {
      navigate('/list')
      return
    }
    setIsLoading(true)
    dispatch(setSelectedUser(Number(userId)))

    dispatch(resetReservationQueryAndIds(initialQuery))
    dispatch(getUserWithId(Number(userId)))
      .catch(() => {
        navigate('/')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [dispatch, userId])

  return isLoading || !user ? (
    <Layout className="min-h-100vh">
      <MainHeader />
      <Spin
        style={{
          top: '50%',
          left: '50%',
          position: 'absolute',
          transform: 'translate(-50%,-50%)',
        }}
      />
    </Layout>
  ) : (
    <Layout>
      <MainHeader />
      <Layout style={{ minHeight: '100vh', marginTop: 80 }}>
        <Content>
          <Flex vertical gap={20} className="container-row mt-0b">
            <Space
              orientation="vertical"
              className="w-full"
              style={{
                position: 'sticky',
                background: token.colorBgLayout,
                zIndex: 200,
                top: 64,
                paddingTop: 20,
              }}
            >
              <Space className="align-items-center w-full space-between">
                <Space>
                  <User.Avatar
                    photoUrl={user.sevenroomsProfile?.photo}
                    model={user}
                    type="condensed"
                    size={35}
                    noLinkToDetails
                    pictureOpensInModal
                  />
                  <Title
                    className="m-0"
                    level={3}
                  >{`${user.firstName} ${user.lastName}`}</Title>
                </Space>
              </Space>
              <Space></Space>

              <Tabs
                activeKey={selectedKey}
                items={items}
                onChange={(key) => {
                  setSelectedKey(key)
                  navigate(key)
                }}
              />
            </Space>
            <Flex vertical>
              <Outlet />
            </Flex>
          </Flex>
        </Content>
        <LayoutFooter />
      </Layout>
    </Layout>
  )
}
